import { useAnnotationStore } from "@frontend/stores/annotation-store"
import {
  bytesToRuns,
  diffToManifest,
  encodeLabelRasterManifest,
  type LabelRasterDiffLike,
  manifestRunBytes
} from "@shared/extensions/label-raster/manifest"
import type { LabelRasterAnnotationState, LabelRasterManifestEntry } from "@shared/lib/history-types"

/** Warn (once per save) when the sparse edit set gets large enough to slow saves. */
export const LABEL_RASTER_SIZE_WARNING_BYTES = 4 * 1024 * 1024

export function labelRasterStateToDiff(state: LabelRasterAnnotationState): LabelRasterDiffLike {
  return {
    version: 1,
    source: {
      seriesId: state.seriesId,
      arrayPath: state.source.arrayPath,
      shape: [state.source.shape[0], state.source.shape[1]],
      chunkShape: [state.source.chunkShape[0], state.source.chunkShape[1]],
      dtype: state.source.dtype,
      axes: ["y", "x"],
      locator: state.source.locator,
      taxonomy: state.source.taxonomy,
      pipelineVersion: state.source.pipelineVersion
    },
    chunks: state.chunks.map((c) => ({
      index: [c.index[0], c.index[1]],
      originalCrc32: c.originalCrc32,
      runs: bytesToRuns(c.runs),
      changedPixels: c.changedPixels,
      bbox: [c.bbox[0], c.bbox[1], c.bbox[2], c.bbox[3]]
    })),
    classesUsed: [...state.classesUsed],
    editedAt: state.editedAt
  }
}

/**
 * Build the session-level encoded manifests from the current annotation store.
 * Synchronous and cheap: derived purely from the sparse runs in the snapshot.
 */
export function buildLabelRasterManifestEntries(): LabelRasterManifestEntry[] {
  const rasters = useAnnotationStore.getState().labelRasters
  const entries: LabelRasterManifestEntry[] = []
  let totalBytes = 0
  for (const raster of rasters) {
    if (raster.chunks.length === 0) continue
    const manifest = diffToManifest(labelRasterStateToDiff(raster), { editorVersion: "web" })
    totalBytes += manifestRunBytes(manifest)
    entries.push({ annotationId: raster.id, manifest: encodeLabelRasterManifest(manifest) })
  }
  if (totalBytes > LABEL_RASTER_SIZE_WARNING_BYTES) {
    console.warn(
      `[label-raster] edit set is ${(totalBytes / 1024 / 1024).toFixed(1)} MB across ${entries.length} manifests; saves may be slow`
    )
  }
  return entries
}
